import { useGlobalToast } from '@/components/src';
import { useTranslation } from '@/helpers/src';
import { storeToRefs } from 'pinia';
import type { MenuScheduleFormValues } from '../../../routes/station-schedule/components/schedule-panel/menu-schedule-panel/types';
import {
  isCalendarRevisionConflictError,
  useCalendarStore,
  useStationScheduleStore,
  useStationStore,
} from '../../../routes/stores';
import useCalendar from '../use-calendar';
import { useMenuStore } from '@centric-os/stores/menus/use-menu-store';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import type { CalendarEvent, Menu } from '@centric-os/types';
import { SitesLocalePrefix } from '../../../enums/locale-prefixs';
import { computed } from 'vue';
import { cloneDeep, isNil } from 'lodash-es';
import { useMenuSchedule } from '../../../routes/station-schedule/components/schedule-panel/menu-schedule-panel/menu-form-grid/composables';
import useCalendarRevisionConflictFeedback from '../use-calendar-revision-conflict-feedback';

export default () => {
  // stores
  const calendarStore = useCalendarStore();
  const { currentEvent, isNewEvent, structuredStationCalendarEvents } = storeToRefs(calendarStore);
  const siteStore = useSiteStore();
  const { updateSiteOnNextMount } = storeToRefs(siteStore);
  const stationStore = useStationStore();
  const { stationBrand } = storeToRefs(stationStore);
  const stationScheduleStore = useStationScheduleStore();
  const { showMenuScheduleForm, eventFormEdited } = storeToRefs(stationScheduleStore);
  const menuStore = useMenuStore();
  const { v2Menu } = storeToRefs(menuStore);
  const LOCALE_PREFIX = SitesLocalePrefix.SITE_STATION;
  // Set use menu schedule
  const { initialValues } = useMenuSchedule();
  // composables
  const { translate } = useTranslation();
  const { showSuccessToast, showErrorToast } = useGlobalToast();
  const { showCalendarRevisionConflictToast } = useCalendarRevisionConflictFeedback();
  const { prepMenuEventForSave } = useCalendar();

  const saveMenuSchedule = async (formData: MenuScheduleFormValues): Promise<void> => {
    const localFormData = cloneDeep(formData);
    /*
        V4 MENU:
        - If the menu is a v4 menu, we need to call a v4 endpoint to schedule the menu to the station.
        - If we are changing to a different menu, we need to unschedule the old menu and schedule the new menu; 
          or if it's a brand new menu, we need to schedule it.
        - If we are changing the price level, we need to update the menu.
        - If the menu and price level are the same, we don't need to do anything.
      */
    if (localFormData.brand?.published_catalog_id !== undefined) {
      if (initialValues.value.eventMenu?.id !== localFormData.eventMenu.id) {
        // When unscheduling, we must use currentEvent.value.data.menu (the intermediary menu ID stored on the calendar event),
        // NOT initialValues.value.eventMenu.id (the published menu ID).
        //
        // V4 uses an intermediary menu pattern:
        // 1. Users select from published menus to schedule
        // 2. scheduleMenu() creates an intermediary menu that references the published menu
        // 3. Calendar events store the intermediary menu ID in event.data.menu
        // 4. The backend's unschedule endpoint expects the intermediary menu ID to:
        //    - Check if the menu is used by other calendar events
        //    - Soft delete the intermediary menu (not the published menu)
        //
        // Using the published menu ID would cause the backend to delete the published menu itself.
        if (!isNil(initialValues.value.eventMenu?.id)) {
          await menuStore.unscheduleMenu({
            menu_id: currentEvent.value?.data?.menu,
            station_id: stationBrand.value.id,
          });
        }
        const newMenu: Menu = await menuStore.scheduleMenu({
          menu_id: localFormData.eventMenu.id,
          station_id: stationBrand.value.id,
          price_level_id: localFormData.priceLevel,
        });
        localFormData.eventMenu = newMenu;
      } else if (initialValues.value.priceLevel !== localFormData.priceLevel) {
        await menuStore.unscheduleMenu({
          menu_id: currentEvent.value.data.menu,
          station_id: stationBrand.value.id,
        });
        const newMenu: Menu = await menuStore.scheduleMenu({
          menu_id: localFormData.eventMenu.id,
          station_id: stationBrand.value.id,
          price_level_id: localFormData.priceLevel,
        });
        localFormData.eventMenu.id = newMenu.id;
      } else {
        localFormData.eventMenu.id = currentEvent.value.data.menu;
      }
    }

    try {
      currentEvent.value = prepMenuEventForSave(
        localFormData,
        currentEvent.value,
        calendarStore.stationTimezone(),
      );
    } catch {
      showErrorToast(translate(`${LOCALE_PREFIX}.menuSchedule.eventDurationError`));
      return;
    }

    try {
      // When scheduling a Centric Menu, if the station is not a centric station, set it to centric
      if (isNewEvent.value && !stationBrand.value?.is?.cafe360) {
        stationBrand.value.is.cafe360 = true;
        await stationStore.updateStationBrand(stationBrand.value);
      }

      await addStationToV2Menu(currentEvent.value);
      await calendarStore.saveCalendarEvents(stationBrand.value?.id, true, false);
      const activeCalendarEventIds = Object.keys(structuredStationCalendarEvents.value);
      await calendarStore.getAllLocationCalendarEvents(activeCalendarEventIds, true);
      //show success
      showSuccessToast(translate(`${LOCALE_PREFIX}.menuSchedule.calendarSaveSuccess`));
      updateSiteOnNextMount.value = true;
      showMenuScheduleForm.value = false;
      currentEvent.value = null;
      eventFormEdited.value = null;
    } catch (err) {
      if (isCalendarRevisionConflictError(err)) {
        showCalendarRevisionConflictToast();
        return;
      }
      showErrorToast(translate(`${LOCALE_PREFIX}.menuSchedule.calendarSaveError`));
    }
  };

  // add location_brand to menu as a temp fix for Nextep integration.
  const addStationToV2Menu = async (event: CalendarEvent): Promise<void> => {
    const v2MenuId = event.data?.menu;

    if (!v2MenuId) return;

    const queryOptions = {
      extended: true,
      _query: '{id,location_brand}',
    };

    await menuStore.getV2Menu(v2MenuId, queryOptions);

    const currentLocationBrand = v2Menu.value.location_brand;
    const stationBrandId = stationBrand.value?.id;

    if (currentLocationBrand !== stationBrandId) {
      await menuStore.patchV2Menu(v2MenuId, { location_brand: stationBrandId });

      // Trigger cache refresh without waiting for it
      menuStore.getV2Menu(v2MenuId, {
        ...queryOptions,
        nocache: true,
      });
    }
  };

  const deleteMenuSchedule = async (): Promise<void> => {
    try {
      // Call the store method to handle the deletion
      await stationScheduleStore.deleteMenuSchedule();

      // Show success toast
      showSuccessToast(translate(`${LOCALE_PREFIX}.menuSchedule.calendarSaveSuccess`));
    } catch (err) {
      if (isCalendarRevisionConflictError(err)) {
        showCalendarRevisionConflictToast();
        return;
      }
      console.error('Failed deleting menu schedule:', err);
      showErrorToast(translate(`${LOCALE_PREFIX}.menuSchedule.calendarSaveError`));
    }
  };

  const getMenuEvents = (): CalendarEvent[] => {
    if (structuredStationCalendarEvents.value?.[stationBrand.value?.id]?.menuEvents) {
      const events = Object.values(
        structuredStationCalendarEvents.value?.[stationBrand.value?.id]?.menuEvents,
      )?.flatMap((arr) => arr);
      return events;
    }
    return [];
  };
  // Computed for the header text
  const getHeaderText = computed<string>(() => {
    if (isNewEvent.value) {
      return translate(`${LOCALE_PREFIX}.menuSchedule.sideBar.createTitle`);
    }
    return translate(`${LOCALE_PREFIX}.menuSchedule.sideBar.editTitle`);
  });

  return {
    saveMenuSchedule,
    deleteMenuSchedule,
    getMenuEvents,
    getHeaderText,
  };
};
